/**
 * Content viewer — middle/right column of the FULL layout. Renders the
 * selected playlist item as iframe (vimeo / youtube / wistia / loom /
 * vidyard / brightcove via server-resolved `embed_url`), <img> for
 * images, <video> for R2-hosted MP4/WebM files, or a sandboxed iframe
 * fallback for HTML/PDF/web-page content. Verbatim port of legacy
 * `ContentPanel.vue`. Real PdfViewer is heavier (canvas + pdfjs-dist);
 * left as a deferred slice — for now PDFs render via iframe fallback.
 *
 * Second leaf migrated to Preact (branch `widget/preact-rewrite`). The
 * controller API (createContentPanel → {el, setContent, setNav, onPrev,
 * onNext}) is preserved so ui.tsx integration stays unchanged; internals
 * are now signals-backed Preact.
 */

import { effect, signal } from '@preact/signals';
import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { mountPreactIsland } from '../voice/preact-mount';
import { botTalking } from '../voice/voice-state';
import { playDuckedIn } from '../tours/audio-duck';
import {
  embedCommand,
  embedHandshakes,
  embedProviderOf,
  mediaFloor,
  parseEmbedMessage,
} from '../media-floor';
import { loadTours } from '../tours/tours-loader';
import { getOrCreateVisitorFingerprint } from '../visitor-id';
import type { PlaylistItem } from './playlist-drawer';

/** A consumption-depth milestone on an agent-delivered asset. */
export interface ContentProgress {
  contentId: string;
  contentType: string;
  /** Asset display name — rides to the GTM dataLayer bridge. */
  title?: string;
  kind: 'video' | 'scroll';
  /** Depth reached, 0–100. */
  percent: number;
  /** For video: seconds watched at this milestone. */
  watchedSecs?: number;
  /** True at the completion milestone (≥90% / ended). */
  done?: boolean;
}

type ProgressFn = (p: ContentProgress) => void;

/** A request to move the open viewer. `nonce` distinguishes repeat seeks
 *  to the same target so the video effect re-fires. */
type SeekReq = { page?: number; timestampSecs?: number; nonce: number };

export interface ContentPanelController {
  el: HTMLElement;
  /** Show an asset. `opts.page` (PDF) / `opts.timestampSecs` (video) open
   *  it AT that location on the initial mount — a single fresh load, the
   *  reliable way to land on a PDF page. */
  setContent(it: PlaylistItem | null, opts?: { page?: number; timestampSecs?: number }): void;
  setNav(opts: { hasPrev: boolean; hasNext: boolean }): void;
  onPrev(h: () => void): void;
  onNext(h: () => void): void;
  /** Consumption-depth milestones (currently native <video>; YouTube/Vimeo/
   *  PDF need player APIs — deferred). */
  onProgress(h: ProgressFn): void;
  /** Drive the open viewer to a PDF page or a video timestamp, in place.
   *  No-op for non-seekable content (remote embeds, images). */
  seekTo(req: { page?: number; timestampSecs?: number }): void;
}

/** Depth milestones we emit at, in order. Completion = the ≥90 crossing. */
const VIDEO_MILESTONES = [25, 50, 75, 90, 100] as const;

/**
 * Native <video> with consumption-depth instrumentation. Fires `onProgress`
 * once per crossed milestone (deduped), and marks `done` at ≥90%. No per-tick
 * spam — `timeupdate` is throttled to milestone crossings only.
 */
function VideoViewer({
  content,
  onProgress,
  seek,
}: {
  content: PlaylistItem;
  onProgress?: ProgressFn;
  seek?: SeekReq | null;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  // Agent-driven seek. Apply once metadata is available (setting
  // currentTime before `loadedmetadata` is dropped by the browser), then
  // play from there — when the agent says "let me jump you to the demo",
  // the visitor expects it to start playing at that point.
  useEffect(() => {
    const el = ref.current;
    if (!el || !seek || seek.timestampSecs === undefined) return;
    const target = seek.timestampSecs;
    const apply = () => {
      try {
        el.currentTime = target;
        void el.play().catch(() => {});
      } catch {
        /* seek out of range / not ready — ignore */
      }
    };
    if (el.readyState >= 1) apply();
    else el.addEventListener('loadedmetadata', apply, { once: true });
    return () => el.removeEventListener('loadedmetadata', apply);
    // Keyed on nonce so a repeat seek to the same timestamp re-fires.
  }, [seek?.nonce]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !onProgress) return;
    const fired = new Set<number>();
    const contentId = String(content.content_id ?? '');
    const contentType = content.content_type ?? 'video';
    const onTime = () => {
      if (!el.duration || !Number.isFinite(el.duration)) return;
      const pct = Math.min(100, (el.currentTime / el.duration) * 100);
      for (const m of VIDEO_MILESTONES) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          onProgress({
            contentId,
            contentType,
            title: content.name,
            kind: 'video',
            percent: m,
            watchedSecs: Math.round(el.currentTime),
            done: m >= 90,
          });
        }
      }
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onTime);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onTime);
    };
    // Re-arm per asset (new content = fresh milestone set).
  }, [content.content_id, onProgress]);

  // MEDIA FLOOR (one-voice law): a native video is narrated media — the
  // agent speaking pauses it, standing down resumes it, and `ended` is
  // the agent's cue to retake the thread.
  useEffect(() => {
    const el = ref.current;
    const mf = mediaFloor();
    if (!el || !mf) return;
    // Restart the way a tour clip does: from a ducked level, ramping up
    // (audio-duck.ts) — a late tail of the agent's voice never collides.
    let cancelDuckIn: (() => void) | null = null;
    const unregister = mf.register({
      pause: () => {
        cancelDuckIn?.();
        cancelDuckIn = null;
        el.pause();
      },
      resume: () => {
        cancelDuckIn?.();
        cancelDuckIn = playDuckedIn(el, el.volume);
      },
    });
    const onPlay = () => mf.notePlaying(true);
    const onPause = () => mf.notePlaying(false);
    const onEnded = () => mf.noteEnded(String(content.content_id ?? ''), content.name ?? null);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    return () => {
      cancelDuckIn?.();
      unregister();
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [content.content_id]);

  return (
    <video
      ref={ref}
      class="sp-content-video"
      src={content.url ?? ''}
      poster={content.image ?? undefined}
      controls
      playsInline
      // Autoplay on open — the agent opened this for the visitor, so start
      // it rolling. Browsers may block autoplay-with-sound without a prior
      // gesture; in an active chat/voice session there usually is one, and
      // the controls are always there as a fallback.
      autoPlay
    />
  );
}

type NavState = { hasPrev: boolean; hasNext: boolean };

/** Direct-file video extensions we can render with a native <video>. */
const VIDEO_FILE_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;

/** Build the PDF iframe src. Always hides the browser's built-in PDF
 *  chrome (`toolbar/navpanes/scrollbar=0`) so the viewer feels native to
 *  the experience instead of showing Chrome's dark toolbar with
 *  rotate/annotate/print/download — the AGENT drives page navigation via
 *  content_seek, so those controls don't belong here. When a page is
 *  given, a fragment-only change (`…#page=N`) is a same-document nav the
 *  browser ignores, so we ALSO bump a query param (`sp_pg`) to force the
 *  reload that makes `#page` land. Reusing the SAME iframe element
 *  (stable key) keeps the container size stable so the viewer doesn't
 *  collapse or reset zoom. */
const PDF_VIEW_PARAMS = 'toolbar=0&navpanes=0&scrollbar=0';
export function withPdfPage(url: string, page?: number): string {
  const noHash = url.split('#')[0] ?? url;
  if (!page) return `${noHash}#${PDF_VIEW_PARAMS}`;
  const sep = noHash.includes('?') ? '&' : '?';
  return `${noHash}${sep}sp_pg=${page}#page=${page}&${PDF_VIEW_PARAMS}`;
}

/** Append `autoplay=1` so provider embeds (YouTube / Vimeo / Wistia)
 *  start on open. Autoplay-with-sound may still be blocked by browser
 *  policy — best-effort, the player controls remain. */
export function withEmbedAutoplay(url: string): string {
  if (!url || /[?&]autoplay=/.test(url)) return url;
  return url + (url.includes('?') ? '&' : '?') + 'autoplay=1';
}

function withParam(url: string, param: string): string {
  const key = param.split('=')[0]!;
  if (!url || new RegExp(`[?&]${key}=`).test(url)) return url;
  return url + (url.includes('?') ? '&' : '?') + param;
}

/**
 * Remote video embed (vimeo / youtube / wistia / loom / vidyard /
 * brightcove — server-side `parseRemoteVideo` produced the iframe-ready
 * URL). `autoplay=1` so it starts when the agent opens it. Own class
 * (NOT sp-content-iframe): remote players must keep 16:9 — a full-pane
 * iframe stretches the player to the pane's shape.
 *
 * MEDIA FLOOR (one-voice law): YouTube (`enablejsapi=1`) and Vimeo speak
 * a postMessage protocol — we get playing/paused/ENDED events and can
 * pause/resume, so they join the floor like native video. The long tail
 * has no protocol we speak: `holdWhileMounted` — the AGENT holds the
 * silence while one is on stage.
 */
function RemoteEmbedViewer({ content, embedUrl }: { content: PlaylistItem; embedUrl: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const provider = embedProviderOf(embedUrl);
  let src = withEmbedAutoplay(embedUrl);
  if (provider === 'youtube') src = withParam(src, 'enablejsapi=1');

  useEffect(() => {
    const mf = mediaFloor();
    if (!mf) return;
    if (!provider) return mf.holdWhileMounted();

    const frame = frameRef.current;
    if (!frame) return;
    const post = (msg: string) => frame.contentWindow?.postMessage(msg, '*');
    const unregister = mf.register({
      pause: () => post(embedCommand(provider, 'pause')),
      resume: () => post(embedCommand(provider, 'play')),
    });
    const onLoad = () => embedHandshakes(provider).forEach(post);
    // The iframe may already be loaded by the time this effect runs.
    onLoad();
    frame.addEventListener('load', onLoad);
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return;
      const ev = parseEmbedMessage(e.data);
      if (ev === 'playing') mf.notePlaying(true);
      else if (ev === 'paused') mf.notePlaying(false);
      else if (ev === 'ended') mf.noteEnded(String(content.content_id ?? ''), content.name ?? null);
    };
    window.addEventListener('message', onMessage);
    return () => {
      unregister();
      frame.removeEventListener('load', onLoad);
      window.removeEventListener('message', onMessage);
    };
  }, [content.content_id, provider]);

  return (
    <div class="sp-content-embed-shell">
      <iframe
        ref={frameRef}
        class="sp-content-embed-video"
        src={src}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function EmptyState() {
  return (
    <>
      <div class="sp-content-header sp-content-header--empty">
        <span class="sp-content-empty-label">Select content</span>
      </div>
      <div class="sp-content-body sp-content-body--empty">
        <div class="sp-content-fallback">
          <div class="sp-content-fallback-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="7" height="7" x="3" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="3" rx="1" />
              <rect width="7" height="7" x="3" y="14" rx="1" />
              <rect width="7" height="7" x="14" y="14" rx="1" />
            </svg>
          </div>
          <p class="sp-content-fallback-text">Select content from playlist</p>
        </div>
      </div>
    </>
  );
}

function PreviewUnavailable({ url }: { url: string | null }) {
  return (
    <div class="sp-content-fallback">
      <div class="sp-content-fallback-icon">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" />
          <path d="M13 2V9H20" />
        </svg>
      </div>
      <p class="sp-content-fallback-text">Content preview not available</p>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener"
          class="sp-content-fallback-link"
        >
          Open in new tab
        </a>
      ) : null}
    </div>
  );
}

const TOUR_URL_RE = /\/t\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * A TOUR as content: the pane hosts the SAME tour player the public
 * page and launcher use (lazy tours chunk, embed mode, autoplay) — no
 * nested page, and the tour narrates itself. If the chunk can't load,
 * the public tour page is iframed instead (`url` is `/t/<id>?embed=1`).
 * Unmounting the pane tears the island down so audio/tape stop.
 */
function TourViewer({ content }: { content: PlaylistItem }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);
  const publicId =
    content.content_public_id ?? TOUR_URL_RE.exec(content.url ?? '')?.[1] ?? null;
  const apiBase =
    (typeof window !== 'undefined' ? window.SHOWPILOT_CONFIG?.apiBaseUrl : undefined) ?? '';

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !publicId) return;
    let cancelled = false;
    host.setAttribute('data-showpilot-tour', publicId);
    host.setAttribute('data-showpilot-tour-mode', 'embed');
    host.setAttribute('data-showpilot-tour-autoplay', 'true');
    if (apiBase) host.setAttribute('data-showpilot-api', apiBase);
    // The content pane is an AGENT surface — presence law (2026-08-24):
    // any tour here is agent-led. Presentation mode always: cards off,
    // highlight ring, the agent drives through the control facade (and
    // the tour's own narration stands down — one-voice law; the agent
    // is the voice-over). Self-play stays what /t/ pages and launchers
    // render outside the widget.
    host.setAttribute('data-showpilot-tour-presentation', 'true');
    // The pane is the 'chat' surface in Insights; the visitor id stitches
    // the play to the chat session's visitor.
    let visitorId: string | undefined;
    try {
      visitorId = getOrCreateVisitorFingerprint();
    } catch {
      visitorId = undefined;
    }
    loadTours()
      .then((bootTour) => {
        if (!cancelled)
          bootTour(host, {
            playerSource: 'chat',
            visitorId,
            // AUDIO DUCK bridge (one-voice law, source audio): the tours
            // chunk can't track this bundle's signals (separate
            // @preact/signals copies), so it gets edges via callback.
            // Ducks tour-video source audio while the agent is audibly
            // talking. effect() from @preact/signals re-runs on change.
            subscribeBotAudible: (cb) => effect(() => cb(botTalking.value)),
          });
      })
      .catch(() => {
        if (!cancelled) setFallback(true);
      });
    return () => {
      cancelled = true;
      // bootTour rendered a Preact island into the host — unmount it so
      // TourWindow's cleanup (tape, audio, observers) actually runs.
      render(null, host);
      host.removeAttribute('data-showpilot-tour-initialized');
    };
  }, [publicId, apiBase]);

  if (!publicId || fallback) {
    return content.url ? (
      <iframe
        class="sp-content-iframe"
        src={content.url}
        allow="autoplay; fullscreen; clipboard-write"
        title={content.name || 'Tour'}
      />
    ) : (
      <PreviewUnavailable url={null} />
    );
  }
  return <div ref={hostRef} class="sp-content-tour" />;
}

function Viewer({
  content,
  onProgress,
  seek,
}: {
  content: PlaylistItem;
  onProgress?: ProgressFn;
  seek?: SeekReq | null;
}) {
  const rawUrl = content.url ?? '';
  const embedUrl = content.embed_url ?? '';
  const ext = content.content_extension;

  if (ext === 'tour' || content.content_type === 'tour') {
    return <TourViewer content={content} />;
  }

  if (embedUrl) {
    return <RemoteEmbedViewer content={content} embedUrl={embedUrl} />;
  }

  if (ext === 'image') {
    return <img src={rawUrl} class="sp-content-image" alt={content.name} />;
  }

  if (ext === 'video' && VIDEO_FILE_RE.test(rawUrl)) {
    // R2-hosted file → native <video> with consumption-depth tracking.
    return <VideoViewer content={content} onProgress={onProgress} seek={seek} />;
  }

  if (rawUrl) {
    // PDFs need the browser's native PDF viewer, which the `sandbox`
    // attribute blocks (plugins-not-allowed). Use a plain `<iframe>`
    // for PDFs so Chrome / Firefox / Safari can render them, and the
    // sandboxed variant for everything else (html, web page, article).
    const isPdf = ext === 'pdf' || /\.pdf(\?|#|$)/i.test(rawUrl);
    if (isPdf) {
      // Agent-driven page seek. `withPdfPage` bumps a query param so the
      // browser reloads (a fragment-only change wouldn't), then `#page=N`
      // lands the viewer. Key is stable PER ASSET (not per page) so a page
      // change REUSES the same iframe element — no unmount/remount, so the
      // container doesn't collapse and the viewer keeps its zoom. Switching
      // ASSETS changes the key and cleanly remounts.
      // Always route through withPdfPage so the browser chrome is hidden
      // on the initial open too (not only after a seek).
      const src = withPdfPage(rawUrl, seek?.page);
      return (
        <iframe
          key={`pdf-${content.content_id ?? ''}`}
          class="sp-content-iframe"
          src={src}
          allow="autoplay"
          title={content.name || 'PDF'}
        />
      );
    }
    return (
      <iframe
        class="sp-content-iframe"
        src={rawUrl}
        allow="autoplay"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      />
    );
  }

  return <PreviewUnavailable url={null} />;
}

function ContentPanelView({
  content,
  nav,
  onPrev,
  onNext,
  onProgress,
  seek,
}: {
  content: PlaylistItem | null;
  nav: NavState;
  onPrev: () => void;
  onNext: () => void;
  onProgress?: ProgressFn;
  seek?: SeekReq | null;
}) {
  if (!content) return <EmptyState />;

  return (
    <>
      <div class="sp-content-header">
        <div class="sp-content-title">
          <span class="sp-content-name">{content.name}</span>
        </div>
        <div class="sp-content-actions">
          <button
            class="sp-content-nav-btn"
            disabled={!nav.hasPrev}
            title="Previous"
            onClick={onPrev}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M15 18L9 12L15 6" />
            </svg>
          </button>
          <button
            class="sp-content-nav-btn"
            disabled={!nav.hasNext}
            title="Next"
            onClick={onNext}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9 18L15 12L9 6" />
            </svg>
          </button>
        </div>
      </div>
      <div class="sp-content-body">
        <div class="sp-content-viewer">
          <Viewer content={content} onProgress={onProgress} seek={seek} />
        </div>
      </div>
    </>
  );
}

/**
 * Signals-backed adapter so callers keep the imperative
 * `controller.setContent(...)` style while the rendering goes through
 * Preact. Each setter updates a signal; Preact re-renders.
 */
export function createContentPanel(): ContentPanelController {
  const root = document.createElement('div');
  root.className = 'sp-content-panel';

  const content = signal<PlaylistItem | null>(null);
  const nav = signal<NavState>({ hasPrev: false, hasNext: false });
  // Latest agent-driven seek request. Reset to null whenever the asset
  // changes so a stale page/timestamp never lands on the next asset.
  const seek = signal<SeekReq | null>(null);
  let seekNonce = 0;
  // Handler refs live in mutable closures so the Preact tree can keep
  // a stable callback identity. Replacing the handler doesn't trigger
  // a re-render — the next click just reads the latest ref.
  let prevHandler: (() => void) | null = null;
  let nextHandler: (() => void) | null = null;
  let progressHandler: ProgressFn | null = null;
  // Stable identity so the VideoViewer effect doesn't re-arm every render;
  // it forwards to the latest registered handler.
  const forwardProgress: ProgressFn = (p) => progressHandler?.(p);

  function Mounted() {
    return (
      <ContentPanelView
        content={content.value}
        nav={nav.value}
        onPrev={() => prevHandler?.()}
        onNext={() => nextHandler?.()}
        onProgress={forwardProgress}
        seek={seek.value}
      />
    );
  }

  mountPreactIsland(root, <Mounted />);

  return {
    el: root,
    setContent(it, opts) {
      content.value = it;
      // A location passed with the open (open-at-page / open-at-timestamp)
      // becomes the initial seek so the viewer mounts THERE — one clean
      // load, no open-then-reload. Otherwise drop any pending seek so it
      // can't apply to the wrong content.
      if (opts && (opts.page !== undefined || opts.timestampSecs !== undefined)) {
        seekNonce += 1;
        seek.value = { page: opts.page, timestampSecs: opts.timestampSecs, nonce: seekNonce };
      } else {
        seek.value = null;
      }
    },
    setNav({ hasPrev: p, hasNext: n }) {
      nav.value = { hasPrev: p, hasNext: n };
    },
    onPrev(h) {
      prevHandler = h;
    },
    onNext(h) {
      nextHandler = h;
    },
    onProgress(h) {
      progressHandler = h;
    },
    seekTo(req) {
      seekNonce += 1;
      seek.value = {
        page: req.page,
        timestampSecs: req.timestampSecs,
        nonce: seekNonce,
      };
    },
  };
}
